import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Anchor, Clock, MapPin, ArrowUpRight, MessageCircle } from 'lucide-react';
import { api } from '../lib/api';
import { useSettings } from '../context/SettingsContext';
import { ExpandableText } from '../components/ExpandableText';

export default function Trips() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings();

  useEffect(() => {
    api.get('/trips').then(({ data }) => setTrips(data || [])).catch(() => setTrips([])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="pt-24 pb-20">
      <section className="relative">
        <div className="absolute inset-0 hero-radial pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 relative">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#00b4d8] font-semibold mb-3">Oman Trips</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight max-w-3xl">Cross the border. Cruise the fjords.</h1>
          <p className="mt-5 max-w-2xl text-white/75 text-base sm:text-lg leading-relaxed">Full-day Omani dhow cruises, dive trips and coastal escapes — picked up from your hotel in Dubai, Sharjah or Ras Al Khaimah and delivered straight to the Musandam Peninsula.</p>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading && (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-96 rounded-3xl bg-white/5 border border-white/10 animate-pulse" />
              ))}
            </div>
          )}
          {!loading && trips.length === 0 && (
            <div data-testid="trips-empty-state" className="rounded-3xl border border-white/10 bg-white/5 p-12 sm:p-16 text-center">
              <div className="mx-auto h-14 w-14 rounded-2xl bg-[#00b4d8]/15 text-[#00b4d8] inline-flex items-center justify-center mb-5"><Anchor className="h-6 w-6" /></div>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold">Coming soon</h2>
              <p className="mt-3 text-white/60 max-w-md mx-auto">We are preparing the next season of dive trips. Drop us a message and we will be in touch.</p>
              {settings?.whatsapp_url && (
                <a href={settings.whatsapp_url} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex">
                  <Button className="bg-[#c8e600] text-[#0a1628] hover:bg-[#b6d100] rounded-xl"><MessageCircle className="mr-2 h-4 w-4" /> WhatsApp Us</Button>
                </a>
              )}
            </div>
          )}
          {!loading && trips.length > 0 && (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {trips.map((t) => (
                <Card data-testid="trip-card" key={t.id} className="group rounded-3xl border-white/10 bg-white/5 overflow-hidden flex flex-col">
                  <Link to={`/trips/${t.slug}`} className="relative aspect-[4/3] overflow-hidden block">
                    {t.image_url ? (
                      <img src={t.image_url} alt={t.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
                    ) : (
                      <div className="absolute inset-0 bg-[#0d223d]" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/85 via-[#0a1628]/20 to-transparent" />
                    {t.price && (
                      <div className="absolute top-4 right-4 inline-flex flex-col items-end gap-1">
                        {t.compare_at_price && (() => {
                          const p = parseFloat(String(t.price).replace(/,/g, '').match(/\d+(?:\.\d+)?/)?.[0] || 0);
                          const c = parseFloat(String(t.compare_at_price).replace(/,/g, '').match(/\d+(?:\.\d+)?/)?.[0] || 0);
                          if (c > p) {
                            const pct = Math.round(((c - p) / c) * 100);
                            return (
                              <div className="inline-flex items-center gap-1.5">
                                <span className="line-through text-white/60 text-[11px] bg-[#0a1628]/70 rounded-full px-2 py-0.5">{t.compare_at_price}</span>
                                <span className="inline-flex items-center rounded-full bg-red-500/85 text-white font-bold text-[10px] px-1.5 py-0.5">−{pct}%</span>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        <div className="inline-flex items-center rounded-full border border-[#c8e600]/40 bg-[#0a1628]/80 px-3 py-1 text-xs font-semibold text-[#c8e600]" data-testid={`trip-card-price-${t.slug}`}>{t.price}</div>
                      </div>
                    )}
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="font-display text-xl sm:text-2xl font-semibold leading-tight">{t.name}</h3>
                    </div>
                  </Link>
                  <div className="p-5 flex flex-col gap-4 flex-1">
                    <div className="flex flex-wrap items-center gap-3 text-xs text-white/55">
                      {t.duration && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {t.duration}</span>}
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Musandam, Oman</span>
                    </div>
                    {t.short_description && (
                      <ExpandableText
                        text={t.short_description}
                        limit={220}
                        className="text-sm text-white/65 leading-relaxed whitespace-pre-line"
                        testId={`trip-card-desc-${t.slug}`}
                      />
                    )}
                    <div className="mt-auto pt-2 flex items-center gap-2">
                      <Link to={`/trips/${t.slug}`} className="flex-1">
                        <Button data-testid="trip-read-more-button" variant="outline" className="w-full border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-xl">
                          Read More <ArrowUpRight className="ml-1 h-4 w-4" />
                        </Button>
                      </Link>
                      <Link to={`/trips/${t.slug}#booking`} className="flex-1">
                        <Button data-testid="trip-book-now-button" className="w-full bg-[#00b4d8] text-[#0a1628] hover:bg-[#0098b8] font-semibold rounded-xl">
                          Book Now <ArrowUpRight className="ml-1 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
